import {
  chooseHardwareSendCodec,
  h264ProfileHasFleetHardwareEncoder,
  h264ProfileOf,
  type EncodeProfileProbe,
  type SendCodecDecision,
} from './hardwareCodecPolicy';
import { videoCodecFamily } from './preferHardwareVideoCodecs';

type CodecCapability = RTCRtpCodec;
type SenderParameters = RTCRtpSendParameters & { codecs?: CodecCapability[] };
export type SenderCodecPinOptions = {
  guestHardwareDecodeFamilies?: string[];
  preferH264BitrateFloor?: boolean;
  preferHevcFirst?: boolean;
};

/** One serialized sender transaction: verify HEVC, then fall back to negotiated H.264. */
export class SenderCodecPin {
  public async pin(sender: RTCRtpSender, options: SenderCodecPinOptions,
    probe: EncodeProfileProbe | null): Promise<SendCodecDecision<CodecCapability> | null> {
    const parameters = sender.getParameters() as SenderParameters;
    const negotiated = parameters.codecs ?? [];
    if (!parameters.encodings?.length || !negotiated.length) return null;
    // HEVC needs an affirmative guest decode report; missing evidence is not verification.
    const guestFamilies = options.preferHevcFirst
      ? options.guestHardwareDecodeFamilies ?? [] : options.guestHardwareDecodeFamilies;
    let decision = await chooseHardwareSendCodec(negotiated, probe, guestFamilies,
      options.preferH264BitrateFloor === true, options.preferHevcFirst === true);
    if (!decision.codec && options.preferHevcFirst) {
      decision = await this.h264Fallback(negotiated, probe, decision,
        decision.probeUnavailable ? 'hardware-probe-unavailable' : 'no-shared-hardware-codec');
    }
    if (!decision.codec) return decision;
    if (await this.apply(sender, decision.codec)) return { ...decision, pinAccepted: true };
    if (videoCodecFamily(decision.codec.mimeType) === 'h265') {
      decision = await this.h264Fallback(negotiated, probe, decision, 'hevc-pin-rejected');
      if (decision.codec && await this.apply(sender, decision.codec)) return { ...decision, pinAccepted: true };
    }
    // A rejected write cannot be reported as a codec that was applied.
    return { ...decision, codec: null, pinAccepted: false };
  }

  private async h264Fallback(negotiated: CodecCapability[], probe: EncodeProfileProbe | null,
    previous: SendCodecDecision<CodecCapability>, reason: string): Promise<SendCodecDecision<CodecCapability>> {
    const hostH264 = await chooseHardwareSendCodec(negotiated, probe, undefined, true, false);
    const codec = hostH264.codec ?? negotiated.find(candidate => {
      const profile = h264ProfileOf(candidate.sdpFmtpLine);
      return videoCodecFamily(candidate.mimeType) === 'h264' && profile !== undefined
        && h264ProfileHasFleetHardwareEncoder(profile);
    }) ?? null;
    // Fleet H.264 is the compatibility fallback. Retain the measured hardware set:
    // selecting it must not manufacture proof of guest hardware decode.
    return { ...previous, codec, fallbackReason: reason };
  }

  private async apply(sender: RTCRtpSender, codec: CodecCapability): Promise<boolean> {
    const fresh = sender.getParameters() as SenderParameters;
    if (!fresh.encodings?.length) return false;
    fresh.encodings[0].scaleResolutionDownBy = 1;
    fresh.degradationPreference = 'maintain-resolution';
    (fresh.encodings[0] as { codec?: CodecCapability }).codec = codec;
    fresh.encodings[0].active = true;
    try {
      await sender.setParameters(fresh);
      return true;
    } catch (error) {
      console.warn('[WebRtcOptimizationManager] send-codec pin rejected:', error);
      return false;
    }
  }
}
